import { Box, Paper, Typography, useTheme } from '@mui/material';
import type { JourneyTimelineData } from '../../types';
import TileMetaActions from '../TileMetaActions';

type Props = { data: JourneyTimelineData };

const DOT_SIZE = 14;

export default function JourneyTimelineRenderer({ data }: Props) {
  const theme = useTheme();
  const milestoneCount = data.milestones.length;
  const lineInset = milestoneCount > 1 ? `calc(100% / ${milestoneCount * 2})` : '0px';
  const surface = theme.palette.grey[50];

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: surface,
        width: '100%',
        position: 'relative',
      }}
    >
      <TileMetaActions query={data.meta?.query} registry={data.meta?.registry} />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(6, 1fr)' },
          gap: 3,
          mb: 3,
        }}
      >
        {data.summaryStats.map(stat => (
          <Box key={stat.label}>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontWeight: 700,
                letterSpacing: '0.05em',
                display: 'block',
                mb: 0.5,
              }}
            >
              {stat.label}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {stat.value}
            </Typography>
            {stat.detail ? (
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.25 }}>
                {stat.detail}
              </Typography>
            ) : null}
          </Box>
        ))}
      </Box>

      <Box sx={{ px: 1, py: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1 }}>
          {data.milestones.map((milestone, index) => (
            <Box
              key={`${milestone.date ?? milestone.title}-${index}`}
              sx={{ flex: 1, minWidth: 0, px: 0.5, textAlign: 'center' }}
            >
              {milestone.date ? (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 600,
                    display: 'block',
                    whiteSpace: 'normal',
                    lineHeight: 1.3,
                  }}
                >
                  {milestone.date}
                </Typography>
              ) : (
                <Box sx={{ height: 16 }} />
              )}
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: DOT_SIZE,
            mb: 1,
          }}
        >
          {milestoneCount > 1 ? (
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                left: lineInset,
                right: lineInset,
                top: '50%',
                transform: 'translateY(-50%)',
                height: 2,
                backgroundColor: theme.palette.divider,
              }}
            />
          ) : null}
          {data.milestones.map((milestone, index) => (
            <Box
              key={`dot-${milestone.date ?? index}`}
              sx={{
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                position: 'relative',
                zIndex: 1,
              }}
            >
              <Box
                sx={{
                  width: DOT_SIZE,
                  height: DOT_SIZE,
                  borderRadius: '50%',
                  backgroundColor: milestone.isHighlight
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main,
                  border: `2px solid ${surface}`,
                }}
              />
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
          {data.milestones.map((milestone, index) => (
            <Box
              key={`title-${milestone.title ?? index}`}
              sx={{ flex: 1, minWidth: 0, px: 0.5, textAlign: 'center' }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: milestone.isHighlight ? 700 : 600,
                  color: milestone.isHighlight ? 'primary.main' : 'text.primary',
                  display: 'block',
                  whiteSpace: 'normal',
                }}
              >
                {milestone.title}
              </Typography>
              {milestone.detail ? (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {milestone.detail}
                </Typography>
              ) : null}
            </Box>
          ))}
        </Box>
      </Box>

      {data.footer ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2, lineHeight: 1.6 }}>
          {data.footer}
        </Typography>
      ) : null}
    </Paper>
  );
}
